import React, { useState, useRef, useEffect } from "react";
import "./AddNewUser.scss";
import Loader from "../../utils/loader/loader";
import ToastMessage from "../../utils/toasts/toastMessage";
import {
  addNewUser,
  getCSVUploadedData,
} from "../../Redux/Admin_Panel/people/people.action";
import { useDispatch } from "react-redux";
import {
  CLASS_OPTIONS,
  DIVISION_OPTIONS,
  SUBJECT_OPTIONS,
} from "../../utils/subjectBoard";

const ROLES = ["student", "teacher"];

// export const CLASS_OPTIONS = [];
// for (let i = 1; i <= 12; i++) {
//   CLASS_OPTIONS.push(`Class ${i}`);
// }

// export const DIVISION_OPTIONS = Array.from(
//   { length: 26 },
//   (_, i) => String.fromCharCode(65 + i)
// );

// export const SUBJECT_OPTIONS = [
//   "Python", "JavaScript", "C#", "C", "Java", "PHP",
//   "English", "Hindi", "Mathemetics", "Science", "Social Science",
//   "Computer Science", "Sanskrit", "Physical Education", "Art Education",
//   "English (Language And lit.)", "Hindi(Course A)", "Hindi (Course B)",
//   "Physics", "Chemistry", "Biology", "Computer Applications",
//   "Information Technology", "Home Science", "Accountancy",
//   "Bussiness Studies", "Economics", "Engineering Graphics",
//   "Entrepreneurship", "Biotechnology", "Phychology", "History",
//   "Geography", "Political Science", "Sociology", "Philosophy",
//   "Informatics Practices",
// ];

const initialFormData = {
  email: "",
  firstName: "",
  lastName: "",
  schoolName: "",
  classDivisions: [{ grade: "", division: "", subject: [] }],
  role: "student",
};

const validate = (formData) => {
  const errors = {};

  if (!formData.email.trim()) {
    errors.email = "email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = "enter a valid email address.";
  }

  // if (!formData.firstName.trim()) {
  //   errors.firstName = "first name is required.";
  // }

  // if (!formData.lastName.trim()) {
  //   errors.lastName = "last name is required.";
  // }

  if (!formData.firstName.trim()) {
    errors.firstName = "first name is required.";
  } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
    errors.firstName =
      "first name must not contain digits or special characters.";
  }

  if (!formData.lastName.trim()) {
    errors.lastName = "last name is required.";
  } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
    errors.lastName =
      "last name must not contain digits or special characters.";
  }

  if (!formData.schoolName.trim()) {
    errors.schoolName = "school name is required.";
  }

  const classDivisionErrors = formData.classDivisions.map((cd) => {
    const cdErr = {};

    if (!cd.grade?.trim()) {
      cdErr.grade = "class is required.";
    }

    if (!cd.division?.trim()) {
      cdErr.division = "division is required.";
    }

    // Subject validation only for teachers (array-based)
    if (
      formData.role === "teacher" &&
      (!cd.subject || cd.subject.length === 0)
    ) {
      cdErr.subject = "subject is required.";
    }

    return cdErr;
  });

  // Only add if at least one row has errors
  const hasClassDivisionErrors = classDivisionErrors.some(
    (err) => Object.keys(err).length > 0,
  );

  if (hasClassDivisionErrors) {
    errors.classDivisions = classDivisionErrors;
  }

  return errors;
};

// ── SubjectMultiSelect ─────────────────────────────────────────────────────
const SubjectMultiSelect = ({ value = [], onChange, hasError }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (subject) => {
    if (value.includes(subject)) {
      onChange(value.filter((s) => s !== subject));
    } else {
      onChange([...value, subject]);
    }
  };

  return (
    <div className="multi-select-wrapper" ref={wrapperRef}>
      <div
        className={`multi-select-trigger ${hasError ? "input-error" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {value.length === 0 ? (
          <span className="multi-select-placeholder">select subject(s)</span>
        ) : (
          <span className="multi-select-value">{value.join(", ")}</span>
        )}
        <span className="multi-select-arrow">{open ? "▲" : "▾"}</span>
      </div>

      {open && (
        <div className="multi-select-dropdown">
          {SUBJECT_OPTIONS.map((subject) => (
            <label key={subject} className="multi-select-option">
              <input
                type="checkbox"
                checked={value.includes(subject)}
                onChange={() => toggle(subject)}
              />
              <span>{subject}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── AddSingleUser ──────────────────────────────────────────────────────────
const AddSingleUser = ({ onCancel }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const isTeacher = formData.role === "teacher";

  const handleChange = (e) => {
    const { name, value } = e.target;

    // When switching role, reset classDivisions to a single empty entry
    if (name === "role") {
      setFormData((prev) => ({
        ...prev,
        role: value,
        classDivisions: [{ grade: "", division: "", subject: [] }],
      }));
      setErrors((prev) => ({ ...prev, classDivisions: undefined }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleClassDivisionChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = prev.classDivisions.map((cd, i) =>
        i === index ? { ...cd, [field]: value } : cd,
      );
      return { ...prev, classDivisions: updated };
    });

    if (errors.classDivisions?.[index]?.[field]) {
      setErrors((prev) => {
        const updatedCDErrors = (prev.classDivisions || []).map((cdErr, i) =>
          i === index ? { ...cdErr, [field]: "" } : cdErr,
        );
        return { ...prev, classDivisions: updatedCDErrors };
      });
    }
  };

  const handleAddClassDivision = () => {
    setFormData((prev) => ({
      ...prev,
      classDivisions: [
        ...prev.classDivisions,
        { grade: "", division: "", subject: [] },
      ],
    }));
  };

  const handleRemoveClassDivision = (index) => {
    setFormData((prev) => ({
      ...prev,
      classDivisions: prev.classDivisions.filter((_, i) => i !== index),
    }));
    setErrors((prev) => {
      if (!prev.classDivisions) return prev;
      return {
        ...prev,
        classDivisions: prev.classDivisions.filter((_, i) => i !== index),
      };
    });
  };

  const handleAddNewUser = async () => {
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    const roleID = formData.role === "student" ? 1 : 2;

    const payload = {
      email: formData?.email?.trim(),
      first_name: formData?.firstName?.trim(),
      last_name: formData?.lastName?.trim(),
      school: formData?.schoolName?.trim(),

      class_divisions: formData?.classDivisions?.map((cd) => {
        const data = {
          grade: cd?.grade?.trim(),
          division: cd?.division?.trim(),
        };

        // Add subject only for teachers (join array → comma-separated string)
        if (formData.role === "teacher") {
          data.subject = cd?.subject?.join(", ");
        }

        return data;
      }),

      role_id: roleID,
    };

    try {
      const result = await dispatch(addNewUser(payload));
      if (result?.success === true) {
        dispatch(getCSVUploadedData());
        setToastState({
          show: true,
          message: "User added successfully!",
          type: "success",
        });
        setFormData(initialFormData);
        setErrors({});
      } else {
        setToastState({
          show: true,
          message: result?.message || "Something went wrong. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      setToastState({
        show: true,
        message: "An unexpected error occurred.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-user-overlay">
      <div className="add-user-container">
        <div className="top-nav">
          <button className="back-link" onClick={onCancel}>
            ← Back to People
          </button>
        </div>

        <div className="form-card">
          <div className="form-header">
            <span>Add User Details</span>
            <span className="toggle-icon">▲</span>
          </div>

          <div className="form-body">
            {/* Role — at top as a toggle */}
            <div className="form-row">
              <div className="label-part">
                Role <span className="req">*</span>
              </div>
              <div className="input-part">
                <div className="role-toggle">
                  {ROLES.map((role) => (
                    <button
                      key={role}
                      type="button"
                      className={`role-btn ${formData.role === role ? "active" : ""}`}
                      onClick={() =>
                        handleChange({ target: { name: "role", value: role } })
                      }
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className={`form-row ${errors.email ? "has-error" : ""}`}>
              <div className="label-part">
                Email <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="enter email id"
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>
            </div>

            {/* First Name */}
            <div className={`form-row ${errors.firstName ? "has-error" : ""}`}>
              <div className="label-part">
                First Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="enter first name"
                  className={errors.firstName ? "input-error" : ""}
                />
                {errors.firstName && (
                  <span className="error-text">{errors.firstName}</span>
                )}
              </div>
            </div>

            {/* Last Name */}
            <div className={`form-row ${errors.lastName ? "has-error" : ""}`}>
              <div className="label-part">
                Last Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="enter last name"
                  className={errors.lastName ? "input-error" : ""}
                />
                {errors.lastName && (
                  <span className="error-text">{errors.lastName}</span>
                )}
              </div>
            </div>

            {/* School Name */}
            <div className={`form-row ${errors.schoolName ? "has-error" : ""}`}>
              <div className="label-part">
                School Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  placeholder="enter school name"
                  className={errors.schoolName ? "input-error" : ""}
                />
                {errors.schoolName && (
                  <span className="error-text">{errors.schoolName}</span>
                )}
              </div>
            </div>

            {/* Class & Division & subject */}
            <div className="form-row class-division-row">
              {isTeacher ? (
                <div className="label-part">
                  Class &amp; Subject <span className="req">*</span>
                </div>
              ) : (
                <div className="label-part">
                  Class &amp; Division <span className="req">*</span>
                </div>
              )}
              <div className="input-part">
                {formData.classDivisions.map((cd, index) => (
                  <div key={index} className="class-division-entry">
                    <div className="class-division-inputs">
                      {/* ── Grade dropdown ── */}
                      <div className="sub-input-wrap">
                        <div className="select-wrapper">
                          <select
                            value={cd.grade}
                            onChange={(e) =>
                              handleClassDivisionChange(
                                index,
                                "grade",
                                e.target.value,
                              )
                            }
                            className={
                              errors.classDivisions?.[index]?.grade
                                ? "input-error"
                                : ""
                            }
                          >
                            <option value="">class</option>
                            {CLASS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <span className="select-arrow">▾</span>
                        </div>
                        {errors.classDivisions?.[index]?.grade && (
                          <span className="error-text">
                            {errors.classDivisions[index].grade}
                          </span>
                        )}
                      </div>

                      {/* ── Division dropdown ── */}
                      <div className="sub-input-wrap">
                        <div className="select-wrapper">
                          <select
                            value={cd.division}
                            onChange={(e) =>
                              handleClassDivisionChange(
                                index,
                                "division",
                                e.target.value,
                              )
                            }
                            className={
                              errors.classDivisions?.[index]?.division
                                ? "input-error"
                                : ""
                            }
                          >
                            <option value="">division</option>
                            {DIVISION_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <span className="select-arrow">▾</span>
                        </div>
                        {errors.classDivisions?.[index]?.division && (
                          <span className="error-text">
                            {errors.classDivisions[index].division}
                          </span>
                        )}
                      </div>

                      {/* ── Subject multi-select (teacher only) ── */}
                      {isTeacher && (
                        <div className="sub-input-wrap">
                          <SubjectMultiSelect
                            value={cd.subject}
                            onChange={(newVal) =>
                              handleClassDivisionChange(
                                index,
                                "subject",
                                newVal,
                              )
                            }
                            hasError={!!errors.classDivisions?.[index]?.subject}
                          />
                          {errors.classDivisions?.[index]?.subject && (
                            <span className="error-text">
                              {errors.classDivisions[index].subject}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* − only for teacher */}
                    {isTeacher && (
                      <button
                        type="button"
                        className="btn-icon btn-remove"
                        onClick={() => handleRemoveClassDivision(index)}
                        title="Remove this class & subject"
                        disabled={formData.classDivisions.length === 1}
                        style={{
                          opacity:
                            formData.classDivisions.length === 1 ? 0.35 : 1,
                          cursor:
                            formData.classDivisions.length === 1
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}

                {/* + only for teacher, at bottom-right */}
                {isTeacher && (
                  <div className="class-division-add-row">
                    <button
                      type="button"
                      className="btn-icon btn-add"
                      onClick={handleAddClassDivision}
                      title="Add another class & division"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                className="btn-create"
                onClick={handleAddNewUser}
                disabled={loading}
              >
                Create User
              </button>
              <button
                className="btn-cancel"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
};

export default AddSingleUser;
